import { Info } from "lucide-react";
import { useEffect, useState } from "react";

import { cn } from "../../lib/cn.js";

/**
 * The ⓘ button that opens/closes a `ReportGuideAccordion`, with the exact tooltip
 * `ecom_code` shows beside its report titles (AntD's default Tooltip — a small dark
 * pill, positioned to the right, with a matching dark arrow).
 *
 * A HAND-ROLLED TOOLTIP, NOT `@radix-ui/react-tooltip` — deliberately. Radix's
 * `TooltipTrigger` closes itself on the trigger's own `onPointerDown`/`onClick` (see its
 * source), which is exactly wrong here: clicking this button must NOT dismiss the
 * tooltip while the pointer is still resting on it — it should instantly relabel itself
 * "Hide Guide & Logic" and stay visible, precisely what AntD's hover-only Tooltip does
 * in `ecom_code` (it has no click-to-dismiss behaviour at all). Tracking "is the pointer
 * over the trigger" ourselves, instead of fighting Radix's built-in dismissal, is what
 * makes a click-while-hovering relabel in place rather than vanish.
 *
 * The tooltip still auto-shows for 5s on every mount (i.e. every time someone lands on
 * this page) to draw first-time attention to the guide, then falls back to normal hover
 * behaviour — it does not nag on every render, only once per page visit.
 */
export function ReportGuideToggleButton({
  showGuide,
  onToggle,
  showLabel = "Click to view Report Guide & Logic",
  hideLabel = "Hide Guide & Logic",
  side = "right",
}: {
  readonly showGuide: boolean;
  readonly onToggle: () => void;
  readonly showLabel?: string;
  readonly hideLabel?: string;
  readonly side?: "top" | "right" | "bottom" | "left";
}): React.JSX.Element {
  const [tooltipOpen, setTooltipOpen] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setTooltipOpen(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  const label = showGuide ? hideLabel : showLabel;

  return (
    <span className="relative inline-flex">
      <button
        type="button"
        onClick={onToggle}
        onMouseEnter={() => setTooltipOpen(true)}
        onMouseLeave={() => setTooltipOpen(false)}
        onFocus={() => setTooltipOpen(true)}
        onBlur={() => setTooltipOpen(false)}
        aria-label={label}
        aria-expanded={showGuide}
        data-testid="report-guide-toggle"
        className={cn(
          "inline-flex size-7 shrink-0 items-center justify-center rounded-full transition-colors",
          showGuide
            ? "bg-primary text-primary-foreground ring-primary/25 shadow-xs ring-2"
            : "bg-muted text-muted-foreground hover:bg-accent hover:text-foreground",
        )}
      >
        <Info className="size-4" />
      </button>

      {tooltipOpen && (
        <span
          role="tooltip"
          data-testid="report-guide-tooltip"
          className={cn(
            "pointer-events-none absolute z-50 rounded-md bg-[#262626] px-2.5 py-1.5 text-xs font-normal whitespace-nowrap text-white shadow-lg",
            side === "right" && "top-1/2 left-[calc(100%+8px)] -translate-y-1/2",
            side === "left" && "top-1/2 right-[calc(100%+8px)] -translate-y-1/2",
            side === "bottom" && "top-[calc(100%+8px)] left-1/2 -translate-x-1/2",
            side === "top" && "bottom-[calc(100%+8px)] left-1/2 -translate-x-1/2",
          )}
        >
          {/* The arrow — a 45°-rotated square of the same colour, half tucked behind the pill. */}
          <span
            aria-hidden="true"
            className={cn(
              "absolute size-2 rotate-45 bg-[#262626]",
              side === "right" && "top-1/2 left-0 -translate-x-1/2 -translate-y-1/2",
              side === "left" && "top-1/2 right-0 translate-x-1/2 -translate-y-1/2",
              side === "bottom" && "top-0 left-1/2 -translate-x-1/2 -translate-y-1/2",
              side === "top" && "bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2",
            )}
          />
          {label}
        </span>
      )}
    </span>
  );
}
